// Type-to-search for a product dropdown. Put <ProductMenuSearch> first inside
// the Select, list `visible` instead of all products, and spread `selectProps`
// onto the Select (or SelectProps of a TextField select).
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import React, { useCallback, useMemo, useState } from "react";

import FilterSearch from "../../../design/filter-search";
import { filterProducts,SearchableProduct } from "../utils/product-search"; // eslint-disable-line sort-imports
import { planner } from "./tokens";

export const useProductMenuSearch = <T extends SearchableProduct>(products: T[], selectedId?: number | "" | null) => {
	const [query, setQuery] = useState("");
	const { visible, isEmpty } = useMemo(() => filterProducts(products, query, selectedId), [products, query, selectedId]);
	const clearQuery = useCallback(() => setQuery(""), []);
	const selectProps = useMemo(
		() => ({
			onOpen: clearQuery,
			onClose: clearQuery,
			// The search box takes focus, not the first product.
			MenuProps: { autoFocus: false },
		}),
		[clearQuery]
	);
	return { query, setQuery, visible, isEmpty, selectProps };
};

// Keys typed into the search stay there: without this the Select's menu
// would treat them as type-ahead and steal focus. Escape still closes the
// menu, and ArrowDown moves into the list.
const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
	if (event.key === "Escape") return;
	event.stopPropagation();
	if (event.key === "ArrowDown") {
		event.preventDefault();
		const firstOption = event.currentTarget.parentElement?.querySelector<HTMLElement>("[role='option']:not([aria-disabled='true'])");
		firstOption?.focus();
	}
};

interface ProductMenuSearchProps {
	query: string;
	onQueryChange: (query: string) => void;
}

export const ProductMenuSearch: React.FC<ProductMenuSearchProps> = ({ query, onQueryChange }) => (
	<ListSubheader onKeyDown={handleSearchKeyDown} sx={{ bgcolor: planner.surface, lineHeight: "normal", py: 1, px: 1.5 }}>
		<FilterSearch searchedValue={query} setSearchedValue={onQueryChange} placeholder="Search products" autoFocus />
	</ListSubheader>
);

// A real <MenuItem> (rather than a fragment) because Select clones each child.
export const ProductMenuEmpty: React.FC = () => (
	<MenuItem disabled sx={{ fontStyle: "italic" }}>
		No products match
	</MenuItem>
);
